import { useEffect, useState } from "react";
import {
  CheckCircle2,
  ShieldCheck,
  XCircle,
} from "lucide-react";
import { useParams } from "react-router-dom";

import SEO from "@/components/site/SEO";
import lmsApi from "@/lib/lmsApi";

export default function CertificateVerify() {
  const { verificationCode } = useParams();

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    lmsApi
      .get(
        `/certificates/verify/${encodeURIComponent(
          verificationCode
        )}`
      )
      .then((response) => {
        setData(response.data);
      })
      .catch((err) => {
        setError(
          err.response?.data?.detail ||
            "We could not verify this certificate."
        );
      })
      .finally(() => {
        setLoading(false);
      });
  }, [verificationCode]);

  return (
    <>
      <SEO
        title="Verify TRAC-Learn Certificate | UKTDA"
        description="Verify a TRAC-Learn completion certificate."
        path={`/verify/certificate/${verificationCode}`}
      />

      <section className="min-h-[70vh] bg-slate-50 py-14 md:py-20">
        <div className="container-rail">
          <div className="mx-auto max-w-2xl rounded-2xl border border-slate-200 bg-white p-7 shadow-sm md:p-10">
            <div className="flex items-center gap-3">
              <ShieldCheck className="h-7 w-7 text-safety" />

              <div>
                <p className="label-eyebrow text-safety">
                  TRAC-Learn
                </p>

                <h1 className="mt-1 font-display text-3xl font-bold text-navy">
                  Certificate verification
                </h1>
              </div>
            </div>

            {loading ? (
              <p className="mt-8 text-slate-600">
                Checking certificate record…
              </p>
            ) : error ? (
              <div className="mt-8 rounded-xl border border-red-200 bg-red-50 p-5 text-red-800">
                {error}
              </div>
            ) : data?.valid ? (
              <div className="mt-8">
                <div className="flex items-center gap-3 rounded-xl border border-emerald-200 bg-emerald-50 p-5 text-emerald-800">
                  <CheckCircle2 className="h-6 w-6" />

                  <div>
                    <p className="font-bold">
                      Valid TRAC-Learn certificate
                    </p>

                    <p className="mt-1 text-sm">
                      This certificate matches an active completion record.
                    </p>
                  </div>
                </div>

                <dl className="mt-8 divide-y divide-slate-100 text-sm">
                  {[
                    [
                      "Certificate number",
                      data.certificate_number,
                    ],
                    [
                      "Learner",
                      data.learner_display_name,
                    ],
                    [
                      "Course",
                      data.course_title,
                    ],
                    [
                      "Version",
                      data.version_number,
                    ],
                    [
                      "Completed",
                      data.completed_at
                        ? new Date(
                            data.completed_at
                          ).toLocaleDateString(
                            "en-GB",
                            {
                              day: "numeric",
                              month: "long",
                              year: "numeric",
                            }
                          )
                        : "—",
                    ],
                    [
                      "Issued",
                      data.issued_at
                        ? new Date(
                            data.issued_at
                          ).toLocaleDateString(
                            "en-GB",
                            {
                              day: "numeric",
                              month: "long",
                              year: "numeric",
                            }
                          )
                        : "—",
                    ],
                  ].map(([label, value]) => (
                    <div
                      key={label}
                      className="grid grid-cols-[150px_1fr] gap-4 py-3"
                    >
                      <dt className="font-semibold text-slate-500">
                        {label}
                      </dt>

                      <dd className="font-semibold text-navy">
                        {value}
                      </dd>
                    </div>
                  ))}
                </dl>

                <p className="mt-6 text-xs leading-relaxed text-slate-500">
                  Public verification deliberately displays only limited
                  learner information. Full learning records remain restricted
                  to authorised TRAC-Learn users.
                </p>
              </div>
            ) : (
              <div className="mt-8 flex items-center gap-3 rounded-xl border border-red-200 bg-red-50 p-5 text-red-800">
                <XCircle className="h-6 w-6" />

                <div>
                  <p className="font-bold">
                    Certificate not valid
                  </p>

                  <p className="mt-1 text-sm">
                    No current valid TRAC-Learn certificate matches this
                    verification code.
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>
    </>
  );
}